import React, { useState } from "react";
import {
  FaSearch,
  FaUserGraduate,
  FaUniversity,
  FaPassport,
  FaCalendarAlt,
  FaCheckCircle,
  FaExclamationCircle,
  FaTimes,
  FaInfoCircle,
  FaArrowLeft,
} from "react-icons/fa";
import { findAppointmentByStudentNumber } from "../firebase";
import "./QueryPage.css";

const QueryPage = ({ onBackToHome }) => {
  const [studentNumber, setStudentNumber] = useState("");
  const [appointmentData, setAppointmentData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchPerformed, setSearchPerformed] = useState(false);

  // eslint-disable-next-line no-unused-vars
  const applicationStatusOptions = [
    { value: "preparing", label: "Hazırlanıyor", color: "#ed8936" },
    { value: "submitted", label: "Gönderildi", color: "#3182ce" },
    { value: "under_review", label: "İnceleniyor", color: "#805ad5" },
    { value: "accepted", label: "Kabul Edildi", color: "#38a169" },
    { value: "rejected", label: "Reddedildi", color: "#e53e3e" },
    { value: "waitlist", label: "Bekleme Listesi", color: "#d69e2e" },
  ];

  const visaStatusOptions = [
    { value: "not_applied", label: "Başvurulmadı", color: "#718096" },
    { value: "preparing", label: "Hazırlanıyor", color: "#ed8936" },
    { value: "submitted", label: "Gönderildi", color: "#3182ce" },
    {
      value: "interview_scheduled",
      label: "Mülakat Planlandı",
      color: "#805ad5",
    },
    { value: "approved", label: "Onaylandı", color: "#38a169" },
    { value: "rejected", label: "Reddedildi", color: "#e53e3e" },
  ];

  const handleInputChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (value.length <= 8) {
      setStudentNumber(value);
      setError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setAppointmentData(null);
    setSearchPerformed(true);

    try {
      if (studentNumber.trim().length !== 8) {
        throw new Error("Öğrenci numarası 8 karakterden oluşmalıdır");
      }

      const result = await findAppointmentByStudentNumber(studentNumber.trim());

      if (!result) {
        setError(
          "Bu numaraya ait randevu kaydı bulunamadı. Lütfen numaranızı kontrol edin."
        );
      } else {
        setAppointmentData(result);
      }
    } catch (err) {
      console.error("Query error:", err);
      setError(
        err.message ||
          "Sorgulama sırasında bir hata oluştu. Lütfen tekrar deneyin."
      );
    }

    setIsLoading(false);
  };

  const formatDate = (date) => {
    if (!date) return "-";
    try {
      return new Date(date).toLocaleDateString("tr-TR");
    } catch {
      return "-";
    }
  };

  const getStatusColor = (status, options) => {
    const statusOption = options.find((option) => option.value === status);
    return statusOption ? statusOption.color : "#718096";
  };

  const getStatusLabel = (status, options) => {
    const statusOption = options.find((option) => option.value === status);
    return statusOption ? statusOption.label : status || "-";
  };

  const resetSearch = () => {
    setStudentNumber("");
    setAppointmentData(null);
    setError("");
    setSearchPerformed(false);
  };

  return (
    <div className="query-page">
      <div className="query-background"></div>

      {/* Back Button */}
      <button className="back-button" onClick={onBackToHome}>
        <FaArrowLeft />
        Ana Sayfaya Dön
      </button>

      <div className="query-container">
        {/* Header */}
        <div className="query-header">
          <div className="query-logo">
            <FaUserGraduate />
          </div>
          {appointmentData && !isLoading ? (
            <>
              <h1>Randevu Bilgileriniz Bulundu</h1>
              <p>Aşağıda randevu bilgileriniz görüntülenmektedir.</p>
            </>
          ) : (
            <>
              <h1>Randevu Sorgulama</h1>
              <p>
                Size verilen 8 haneli öğrenci numaranızı girerek randevu bilgilerinizi
                öğrenebilirsiniz.
              </p>
            </>
          )}
        </div>

        {/* Search Form - Only show if no appointment data */}
        {!appointmentData && (
          <div className="query-form-section">
            <form onSubmit={handleSubmit} className="query-form">
              <div className="form-group">
                <label htmlFor="studentNumber">Öğrenci Numaranız</label>
                <div className="search-input-container">
                  <FaSearch className="search-icon" />
                  <input
                    type="text"
                    id="studentNumber"
                    value={studentNumber}
                    onChange={handleInputChange}
                    placeholder="Örn: AB12CD34"
                    maxLength="8"
                    required
                    className={error ? "error" : ""}
                  />
                </div>
                <small className="input-help">
                  Öğrenci numaranız 8 haneli harf ve rakam kombinasyonudur
                </small>
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="search-button"
                  disabled={isLoading || studentNumber.length !== 8}
                >
                  {isLoading ? "Sorgulanıyor..." : "Randevu Bilgilerini Sorgula"}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <FaExclamationCircle />
            <span>{error}</span>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Randevu bilgileriniz sorgulanıyor...</p>
          </div>
        )}

        {/* Results */}
        {appointmentData && !isLoading && (
          <div className="results-section">
            <div className="results-header">
              <FaCheckCircle className="success-icon" />
              <h2>Randevu Bilgileriniz Bulundu</h2>
              <p>
                Aşağıda randevu bilgilerinizi görebilirsiniz.
              </p>
              <button
                type="button"
                className="reset-button"
                onClick={resetSearch}
                style={{ marginTop: "20px" }}
              >
                <FaTimes />
                Yeni Arama
              </button>
            </div>

            <div className="student-info-card">
              {/* Personal Info */}
              <div className="info-section">
                <h3>
                  <FaUserGraduate className="section-icon" />
                  Kişisel Bilgiler
                </h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Ad Soyad</label>
                    <span>
                      {appointmentData.userData?.firstName} {appointmentData.userData?.lastName}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>E-posta</label>
                    <span>{appointmentData.userData?.email || "-"}</span>
                  </div>
                  <div className="info-item">
                    <label>Telefon</label>
                    <span>{appointmentData.userData?.phone || "-"}</span>
                  </div>
                  <div className="info-item">
                    <label>Öğrenci Numarası</label>
                    <span style={{ fontWeight: "700", color: "#667eea" }}>
                      {appointmentData.ogrenciNumarasi}
                    </span>
                  </div>
                </div>
              </div>

              {/* Appointment Info */}
              <div className="info-section">
                <h3>
                  <FaCalendarAlt className="section-icon" />
                  Randevu Bilgileri
                </h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Randevu Tarihi</label>
                    <span>{formatDate(appointmentData.date?.toDate ? appointmentData.date.toDate() : appointmentData.date)}</span>
                  </div>
                  <div className="info-item">
                    <label>Randevu Saati</label>
                    <span>{appointmentData.time || "-"}</span>
                  </div>
                  <div className="info-item">
                    <label>Durum</label>
                    <span
                      className="status-badge"
                      style={{
                        backgroundColor:
                          appointmentData.status === "confirmed" ? "#48bb78" :
                          appointmentData.status === "cancelled" ? "#fc8181" :
                          appointmentData.status === "completed" ? "#4299e1" : "#718096"
                      }}
                    >
                      {appointmentData.status === "confirmed" ? "Onaylandı" :
                       appointmentData.status === "cancelled" ? "İptal Edildi" :
                       appointmentData.status === "completed" ? "Tamamlandı" : appointmentData.status}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Oluşturulma Tarihi</label>
                    <span>{formatDate(appointmentData.createdAt?.toDate ? appointmentData.createdAt.toDate() : appointmentData.createdAt)}</span>
                  </div>
                </div>
              </div>

              {/* Help Info */}
              <div className="help-section">
                <div className="help-card">
                  <FaInfoCircle className="help-icon" />
                  <div className="help-content">
                    <h4>Yardıma mı ihtiyacınız var?</h4>
                    <p>
                      Başvuru durumunuz hakkında detaylı bilgi almak veya
                      süreçle ilgili sorularınız için danışmanlarımızla
                      iletişime geçebilirsiniz.
                    </p>
                    <div className="contact-info">
                      <span>📞 +49 152 313 90 961</span>
                      <span>📧 bilgi@almanyakampus.com</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* No Results */}
        {searchPerformed && !appointmentData && !isLoading && !error && (
          <div className="no-results">
            <FaExclamationCircle className="no-results-icon" />
            <h3>Sonuç Bulunamadı</h3>
            <p>
              Girdiğiniz numaraya ait bir randevu kaydı bulunamadı. Lütfen numaranızı kontrol
              ederek tekrar deneyin.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default QueryPage;
